import React from 'react';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Link from '@liquidweb-dev/undercurrent/Link';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import LayersIcon from '@liquidweb-dev/undercurrent/icons/lucide/Layers2';
import CloudIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cloud';
import ServerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Server';
import ArrowRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowRight';
import WordpressIcon from '@liquidweb-dev/undercurrent/icons/platform/WordPress';
import CircleGaugeIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleGauge';
import { useSelector } from 'react-redux';
import appConfigSelectors from 'modules/appConfig/selectors';
import CreateItem from './CreateItem';
import HookedLink from './HookedLink';

const CreateItemWrapper = ({ key, href, icon, description, title }) => {
	const cartConfig = useSelector(appConfigSelectors.getCartConfig);
	const wwwHostname = useSelector(appConfigSelectors.getWwwHostname);

	return (
		<CreateItem
			key={key}
			component={Link}
			href={href({ configURL: cartConfig.configURL, wwwHostname })}
			target="_blank"
			icon={icon}
			description={description}
		>
			<Typography variant="label-xl">{title}</Typography>
		</CreateItem>
	);
};

/**
 * Function that returns menu items to ensure proper keyboard navigation.
 * MUI's Select/Menu requires MenuItem components to be direct descendants.
 * Using a function instead of a component maintains this relationship.
 * @see {@link https://github.com/mui/material-ui/issues/31006}
 * @param {{ onClose: () => void }} params
 */
export const createMenuContent = ({ onClose }) => [
	<CreateItemWrapper
		key="vps"
		href={({ configURL }) => `${configURL}/cloud-vps/`}
		icon={
			<LayersIcon
				sx={({ size }) => ({
					height: size.sm,
					width: size.sm,
				})}
			/>
		}
		description="Control, customization, security"
		title="Cloud VPS"
	/>,
	<CreateItemWrapper
		key="cloud"
		href={({ configURL }) => `${configURL}/bare-metal-cloud/`}
		icon={
			<CloudIcon
				sx={({ size }) => ({
					height: size.sm,
					width: size.sm,
				})}
			/>
		}
		description="Reliable, scalable, secure"
		title="Cloud Metal"
	/>,
	<CreateItemWrapper
		key="server"
		href={({ configURL }) => `${configURL}/bare-metal-server/`}
		icon={
			<ServerIcon
				sx={({ size }) => ({
					height: size.sm,
					width: size.sm,
				})}
			/>
		}
		description="Private, secure, fully featured"
		title="Bare Metal Server"
	/>,
	<CreateItemWrapper
		key="gpu"
		href={({ configURL }) => `${configURL}/gpu-accelerated/`}
		icon={
			<CircleGaugeIcon
				sx={({ size }) => ({
					height: size.sm,
					width: size.sm,
				})}
			/>
		}
		description="Powerful, efficient, high-performance"
		title="GPU Accelerated"
	/>,
	<CreateItemWrapper
		key="wordpress"
		href={({ wwwHostname }) => `${wwwHostname}/wordpress-hosting/`}
		icon={
			<WordpressIcon
				sx={({ vars, size }) => ({
					height: size.sm,
					width: size.sm,
					path: {
						fill: vars.palette.uc.icon.main,
					},
				})}
			/>
		}
		description="Optimized, experienced, scalable"
		title="WordPress Hosting"
	/>,
	<Divider key="divider" />,
	<CreateItem
		key="marketplace"
		component={HookedLink}
		href="/shop/marketplace"
		onClick={onClose}
		endIcon={<ArrowRightIcon />}
		sx={{
			borderRadius: 0,
		}}
	>
		Visit Marketplace
	</CreateItem>,
];

/**
 * Component that returns MUI's MenuItem components.
 * @param {Object} props
 * @param {() => void} props.onClose
 */
function CreateMenuContent(props) {
	return createMenuContent(props);
}

export default CreateMenuContent;
